"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "@scott/ui/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { Body } from "../body";
import { CloseIcon } from "../icon/icons/close-icon";
import { TickIcon } from "../icon/icons/tick-icon";

const radioVariants = cva(
  "size-[15px] shrink-0 rounded-full border bg-white p-0 transition-[background-color,border-color,outline-color] duration-150 ease-out focus:outline-none focus-visible:outline-2 focus-visible:outline-black focus-visible:outline-offset-2 active:bg-neutral-light",
  {
    variants: {
      variant: {
        primary: "border-black text-black hover:bg-neutral-lighter",
        neutral: "border-black text-black hover:bg-neutral-lighter",
        invalid:
          "border-danger-medium text-danger-medium hover:bg-danger-lightest",
        disabled:
          "cursor-not-allowed border-neutral-medium bg-neutral-lighter text-neutral-medium hover:bg-neutral-lighter",
      },
      size: {
        sm: "",
        md: "",
        lg: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "sm",
    },
  }
);

export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  status?: "error" | "success";
  message?: string;
}

const RadioGroup = ({
  className,
  status,
  message,
  ref,
  ...props
}: RadioGroupProps & {
  ref?: React.RefObject<React.ElementRef<
    typeof RadioGroupPrimitive.Root
  > | null>;
}) => (
  <div className="flex flex-col gap-1">
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
    {status && (
      <div>
        {status === "error" && (
          <span className="flex items-center gap-1">
            <CloseIcon className="h-3 w-3 rounded-full text-error" size="sm" />
            <Body className="text-error" size="xs">
              {message}
            </Body>
          </span>
        )}
        {status === "success" && (
          <span className="flex items-center gap-1">
            <TickIcon className="h-3 w-3 text-button-success" size="sm" />
            <Body className="text-button-success" size="xs">
              {message}
            </Body>
          </span>
        )}
      </div>
    )}
  </div>
);
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

export interface RadioGroupItemProps
  extends VariantProps<typeof radioVariants>,
    RadioGroupPrimitive.RadioGroupItemProps {
  asChild?: boolean;
  label: string;
  description: string;
}

const RadioGroupItem = ({
  className,
  variant,
  size,
  label,
  description,
  ref,
  ...props
}: RadioGroupItemProps & {
  ref?: React.RefObject<React.ElementRef<
    typeof RadioGroupPrimitive.Item
  > | null>;
}) => {
  const getTextSize = (size: string, type: "label" | "description") => {
    switch (size) {
      case "sm":
        return type === "label" ? "sm" : "xs";
      case "md":
        return type === "label" ? "base" : "sm";
      case "lg":
        return type === "label" ? "lg" : "sm";
      default:
        return type === "label" ? "sm" : "xs";
    }
  };

  const getColor = (variant: string, type: "label" | "description") => {
    switch (variant) {
      case "invalid":
        return type === "label" ? "text-danger-medium" : "text-danger-dark";
      case "disabled":
        return type === "label" ? "text-neutral-medium" : "text-neutral-medium";
      default:
        return type === "label" ? "text-neutral-darkest" : "text-neutral-dark";
    }
  };

  const isDisabled = variant === "disabled" || props.disabled === true;
  const effectiveVariant = isDisabled ? "disabled" : variant;
  const labelSize = getTextSize(size ?? "", "label");
  const descriptionSize = getTextSize(size ?? "", "description");
  const labelColor = getColor(effectiveVariant ?? "", "label");
  const descriptionColor = getColor(effectiveVariant ?? "", "description");

  return (
    /*use of the grid to ensure alignment between the radio and the label, whatever the size*/
    /*use of the label tag to be able to click on the entire component*/
    <label
      className={cn(
        "flex gap-x-2 gap-y-0.5",
        isDisabled && "cursor-not-allowed"
      )}
    >
      <RadioGroupPrimitive.Item
        className={cn(
          radioVariants({ variant: effectiveVariant, size, className })
        )}
        ref={ref}
        {...props}
        disabled={isDisabled}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <span className="block size-[9px] rounded-full bg-current" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      <Body className={`${labelColor} self-center p-0`} size={labelSize}>
        {label}
      </Body>
      <div className="h-0" /> {/* empty cell */}
      <Body className={`${descriptionColor} p-0`} size={descriptionSize}>
        {description}
      </Body>
    </label>
  );
};
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
